// src/App.js - Modified with vocabulary subcategories, auto-save, and multi-course support
import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import VocabularySubcategories from './VocabularySubcategories';
import { VocabularyStorage, QuizAutoSave } from './VocabularyStorage';

function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);

  // Check if Supabase connection is working
  useEffect(() => {
    const checkConnection = async () => {
      try {
        setLoading(true);
        
        // Simple query to test connection
        const { data, error } = await supabase
          .from('categories')
          .select('count', { count: 'exact' })
          .limit(1);
        
        if (error) throw error;
        
        console.log('Supabase connection successful');
        setConnected(true);
      } catch (err) {
        console.error('Supabase connection error:', err);
        setError('Could not connect to the quiz database. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    checkConnection();
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <div className="flex items-center" style={{ justifyContent: 'center', flexDirection: 'column', padding: '40px 0' }}>
            <div style={{ 
              width: '50px', 
              height: '50px', 
              borderWidth: '4px',
              borderStyle: 'solid',
              borderColor: '#e5e7eb',
              borderTopColor: '#1a56db',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ marginTop: '16px' }}>Loading quiz data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="container">
        <div className="card">
          <h1 className="card-title" style={{ color: '#ef4444' }}>Connection Error</h1>
          <p>{error}</p>
          <button
            className="button"
            onClick={() => window.location.reload()}
            style={{ marginTop: '16px' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <header className="header">
        <div className="flex space-between items-center">
          <h1 className="header-title">ASA Sailing Quiz</h1>
          <p>Test your sailing knowledge</p>
        </div>
      </header>
      
      <main>
        {connected ? (
          <SailingQuizSystem />
        ) : (
          <div className="card">
            <p>Unable to load quiz data. Please check your connection and try again.</p>
            <button
              className="button"
              onClick={() => window.location.reload()}
              style={{ marginTop: '16px' }}
            >
              Try Again
            </button>
          </div>
        )}
      </main>
      
      <footer style={{ 
        marginTop: '40px', 
        textAlign: 'center', 
        color: '#6b7280', 
        fontSize: '14px',
        padding: '20px'
      }}>
        <p>ASA Sailing Quiz Application</p>
      </footer>
    </div>
  );
}

// New main component that handles course selection and navigation
function SailingQuizSystem() {
  const [view, setView] = useState('courses'); // 'courses', 'categories'
  const [selectedCourse, setSelectedCourse] = useState(null);

  const handleCourseSelect = (courseCode) => {
    setSelectedCourse(courseCode);
    setView('categories');
  };

  const handleBackToCourses = () => {
    setSelectedCourse(null);
    setView('courses');
  };

  // Render breadcrumb navigation
  const renderBreadcrumb = () => {
    if (view === 'courses') return null;
    
    return (
      <div style={{
        marginBottom: '20px',
        fontSize: '14px',
        color: '#6b7280'
      }}>
        <button
          onClick={handleBackToCourses}
          style={{
            background: 'none',
            border: 'none',
            color: '#1a56db',
            textDecoration: 'underline',
            cursor: 'pointer',
            padding: 0,
            fontSize: '14px'
          }}
        >
          All Courses
        </button>
        {selectedCourse && (
          <>
            <span style={{ margin: '0 8px' }}>→</span>
            <span>{selectedCourse}</span>
          </>
        )}
      </div>
    );
  };

  switch (view) {
    case 'courses':
      return <CourseSelection onCourseSelect={handleCourseSelect} />;
    case 'categories':
      return (
        <>
          {renderBreadcrumb()}
          <SailingQuizApp selectedCourse={selectedCourse} onBackToCourses={handleBackToCourses} />
        </>
      );
    default:
      return <div>Navigation error</div>;
  }
}

// New course selection component
function CourseSelection({ onCourseSelect }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchCourses() {
      try {
        setLoading(true);
        setError(null);
        
        // Get distinct course codes from categories
        const { data, error } = await supabase
          .from('categories')
          .select('course_code')
          .not('course_code', 'is', null);
        
        if (error) throw error;
        
        // Extract unique course codes and create course objects
        const uniqueCourses = [...new Set(data.map(item => item.course_code))].map(courseCode => {
          const courseInfo = getCourseInfo(courseCode);
          return {
            code: courseCode,
            ...courseInfo
          };
        });
        
        setCourses(uniqueCourses);
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('Failed to load available courses. Please try again later.');
      } finally {
        setLoading(false);
      }
    }
    
    fetchCourses();
  }, []);

  // Course information mapping
  const getCourseInfo = (courseCode) => {
    const courseMap = {
      'ASA101': {
        title: 'ASA 101 - Basic Keelboat Sailing',
        description: 'Learn fundamental sailing skills including basic sailing theory, rigging, seamanship, and boat handling.',
        level: 'Beginner',
        color: '#1e40af'
      },
      'ASA103': {
        title: 'ASA 103 - Basic Coastal Cruising',
        description: 'Advanced sailing skills for coastal cruising including navigation, weather, anchoring, and overnight passages.',
        level: 'Intermediate',
        color: '#059669'
      },
      'ASA104': {
        title: 'ASA 104 - Bareboat Cruising',
        description: 'Skills needed to safely act as skipper of a sailing vessel in familiar waters during daylight hours.',
        level: 'Advanced',
        color: '#7c2d12'
      }
    };
    
    return courseMap[courseCode] || {
      title: courseCode,
      description: 'Sailing course content',
      level: 'Unknown',
      color: '#6b7280'
    };
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div style={{ 
          width: '50px', 
          height: '50px', 
          margin: '0 auto',
          borderWidth: '4px', 
          borderStyle: 'solid',
          borderColor: '#e5e7eb',
          borderTopColor: '#1a56db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '16px' }}>Loading available courses...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ backgroundColor: '#fee2e2', borderColor: '#ef4444' }}>
        <h1 className="card-title" style={{ color: '#b91c1c' }}>Error</h1>
        <p>{error}</p>
        <button 
          className="button"
          onClick={() => window.location.reload()}
          style={{ marginTop: '16px' }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="card-title">Select a Course</h2>
      <p style={{ marginBottom: '30px', color: '#6b7280' }}>
        Choose from the following ASA certification courses:
      </p>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '20px'
      }}>
        {courses.map(course => (
          <div 
            key={course.code}
            style={{
              borderWidth: '2px',
              borderStyle: 'solid',
              borderColor: course.color,
              borderRadius: '12px',
              padding: '24px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              backgroundColor: '#ffffff',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            onClick={() => onCourseSelect(course.code)}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
              e.currentTarget.style.backgroundColor = '#f8fafc';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              e.currentTarget.style.backgroundColor = '#ffffff';
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '12px'
            }}>
              <h3 style={{ 
                color: course.color, 
                marginBottom: '8px', 
                fontWeight: 'bold',
                fontSize: '18px',
                margin: 0
              }}>
                {course.title}
              </h3>
              <span style={{
                backgroundColor: course.color,
                color: 'white',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {course.level}
              </span>
            </div>
            <p style={{ 
              color: '#4b5563', 
              fontSize: '14px',
              lineHeight: '1.5',
              margin: 0
            }}>
              {course.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Modified SailingQuizApp component to accept selectedCourse and preserve all existing functionality
function SailingQuizApp({ selectedCourse, onBackToCourses }) {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentQuestions, setCurrentQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [quizMode, setQuizMode] = useState('category'); // 'category', 'quiz', 'results', 'vocab'
  const [results, setResults] = useState({
    correct: 0,
    incorrect: 0,
    incorrectQuestions: []
  });
  const [explanationVisible, setExplanationVisible] = useState(false);
  const [isAnswered, setIsAnswered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoSave, setAutoSave] = useState(null);
  const [vocabularySubcategoryId, setVocabularySubcategoryId] = useState(null);

  // Find the vocabulary category for the selected course
  const vocabularyCategoryId = 9; // This may need to be dynamic per course

  // Load categories when the component mounts or course changes
  useEffect(() => {
    async function fetchCategories() {
      try {
        setLoading(true);
        setError(null);
        
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .eq('course_code', selectedCourse)
          .order('id');
        
        if (error) throw error;
        
        setCategories(data || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load quiz categories. Please try again later.');
      } finally {
        setLoading(false);
      }
    }
    
    if (selectedCourse) {
      fetchCategories();
    }
  }, [selectedCourse]);

  // Cleanup autosave on component unmount
  useEffect(() => {
    return () => {
      if (autoSave) {
        autoSave.stopAutoSave();
      }
    };
  }, [autoSave]);

  // Start a quiz for a specific category
  const startCategoryQuiz = async (categoryId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Get category details
      const { data: category, error: categoryError } = await supabase
        .from('categories')
        .select('*')
        .eq('id', categoryId)
        .single();
      
      if (categoryError) throw categoryError;
      
      // Check if it's the vocabulary category
      if (category.name.toLowerCase().includes('vocabulary')) {
        // Set up for vocabulary subcategory selection
        setSelectedCategory(category);
        setQuizMode('vocab');
        setLoading(false);
        return;
      }
      
      // Get questions with answers for the category
      const { data: questionsData, error: questionsError } = await supabase
        .from('questions')
        .select(`
          id,
          question_text,
          explanation,
          answers(id, answer_text, is_correct)
        `)
        .eq('category_id', categoryId);
      
      if (questionsError) throw questionsError;
      
      // Format questions for the quiz
      const formattedQuestions = questionsData.map(question => {
        // Find the correct answer
        const correctAnswer = question.answers.find(a => a.is_correct)?.answer_text || '';
        
        // Randomize the answer options
        const shuffledAnswers = [...question.answers]
          .sort(() => Math.random() - 0.5)
          .map(a => a.answer_text);
        
        return {
          id: question.id,
          question_text: question.question_text,
          explanation: question.explanation,
          correct_answer: correctAnswer,
          options: shuffledAnswers
        };
      });

      // Randomize the order of questions
      const shuffledQuestions = [...formattedQuestions].sort(() => Math.random() - 0.5);
      
      // Set up the quiz
      setSelectedCategory(category);
      setCurrentQuestions(shuffledQuestions);
      setCurrentQuestionIndex(0);
      setQuizMode('quiz');
      setResults({
        correct: 0,
        incorrect: 0,
        incorrectQuestions: []
      });
      setSelectedAnswer('');
      setIsAnswered(false);
      setExplanationVisible(false);
    } catch (err) {
      console.error('Error starting quiz:', err);
      setError('Failed to start the quiz. Please try again.');
      setQuizMode('category');
    } finally {
      setLoading(false);
    }
  };

  // Start a vocabulary subcategory quiz
  const startVocabularyQuiz = async (subcategoryId, savedSession = null) => {
    try {
      setLoading(true);
      setError(null);
      
      // Find the vocabulary category for the current course
      const vocabularyCategory = categories.find(cat => 
        cat.name.toLowerCase().includes('vocabulary')
      );
      
      if (!vocabularyCategory) {
        throw new Error('Vocabulary category not found for this course');
      }
      
      // Get questions with answers for the vocabulary subcategory
      const { data: questionsData, error: questionsError } = await supabase
        .from('questions')
        .select(`
          id,
          question_text,
          explanation,
          answers(id, answer_text, is_correct)
        `)
        .eq('category_id', vocabularyCategory.id)
        .eq('subcategory', subcategoryId);
      
      if (questionsError) throw questionsError;
      
      // Format and randomize questions for the quiz
      const formattedQuestions = questionsData.map(question => {
        // Find the correct answer
        const correctAnswer = question.answers.find(a => a.is_correct)?.answer_text || '';
        
        // Randomize the answer options
        const shuffledAnswers = [...question.answers]
          .sort(() => Math.random() - 0.5)
          .map(a => a.answer_text);
        
        return {
          id: question.id,
          question_text: question.question_text,
          explanation: question.explanation,
          correct_answer: correctAnswer,
          options: shuffledAnswers
        };
      });

      // Randomize the order of questions
      const shuffledQuestions = [...formattedQuestions].sort(() => Math.random() - 0.5);

      // Initialize autosave
      const newAutoSave = new QuizAutoSave(subcategoryId, savedSession, selectedCourse);
      setAutoSave(newAutoSave);
      
      // Set up the quiz
      setSelectedCategory(vocabularyCategory);
      setVocabularySubcategoryId(subcategoryId);
      setCurrentQuestions(shuffledQuestions);
      
      // If resuming a session, restore the state
      if (savedSession) {
        setCurrentQuestionIndex(savedSession.currentQuestionIndex || 0);
        setResults({
          correct: savedSession.correct || 0,
          incorrect: savedSession.incorrect || 0,
          incorrectQuestions: savedSession.incorrectQuestions || []
        });
      } else {
        setCurrentQuestionIndex(0);
        setResults({
          correct: 0,
          incorrect: 0,
          incorrectQuestions: []
        });
      }
      
      setQuizMode('quiz');
      setSelectedAnswer('');
      setIsAnswered(false);
      setExplanationVisible(false);
      
    } catch (err) {
      console.error('Error starting vocabulary quiz:', err);
      setError('Failed to start the vocabulary quiz. Please try again.');
      setQuizMode('vocab');
    } finally {
      setLoading(false);
    }
  };

  // Handler for selecting a vocabulary subcategory
  const handleVocabularySubcategorySelect = (subcategoryId, savedSession = null) => {
    if (subcategoryId === 'daily') {
      // Handle daily review mode (to be implemented)
      alert('Daily review mode will be available soon!');
      return;
    }
    
    if (subcategoryId === 'back') {
      // Go back to main categories screen
      setQuizMode('category');
      setSelectedCategory(null);
      return;
    }
    
    startVocabularyQuiz(subcategoryId, savedSession);
  };

  // Start a quiz with only previously incorrect questions
  const startReviewQuiz = () => {
    if (results.incorrectQuestions.length > 0) {
      setCurrentQuestions([...results.incorrectQuestions]);
      setCurrentQuestionIndex(0);
      setQuizMode('quiz');
      setResults({
        correct: 0,
        incorrect: 0,
        incorrectQuestions: []
      });
      setSelectedAnswer('');
      setIsAnswered(false);
      setExplanationVisible(false);
      
      // If this is a vocab quiz, reinitialize autosave
      if (vocabularySubcategoryId) {
        const newAutoSave = new QuizAutoSave(vocabularySubcategoryId, null, selectedCourse);
        setAutoSave(newAutoSave);
      }
    }
  };

  // Handle answer selection
  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
  };

  // Submit the selected answer
  const submitAnswer = () => {
    const currentQuestion = currentQuestions[currentQuestionIndex];
    const isCorrect = selectedAnswer === currentQuestion.correct_answer;
    
    setIsAnswered(true);
    
    // Update results
    const newResults = {
      ...results,
      correct: isCorrect ? results.correct + 1 : results.correct,
      incorrect: !isCorrect ? results.incorrect + 1 : results.incorrect,
      incorrectQuestions: !isCorrect 
        ? [...results.incorrectQuestions, currentQuestion]
        : results.incorrectQuestions
    };
    
    setResults(newResults);
    
    // Update autosave state if this is a vocabulary quiz
    if (vocabularySubcategoryId && autoSave) {
      autoSave.updateState({
        currentQuestionIndex,
        answers: [...(autoSave.getState().answers || []), {
          questionId: currentQuestion.id,
          answer: selectedAnswer,
          isCorrect
        }],
        results: newResults
      });
    }
  };

  // Move to the next question
  const nextQuestion = () => {
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer('');
      setIsAnswered(false);
      setExplanationVisible(false);
      
      // Update autosave state if this is a vocabulary quiz
      if (vocabularySubcategoryId && autoSave) {
        autoSave.updateState({
          currentQuestionIndex: currentQuestionIndex + 1
        });
      }
    } else {
      // Quiz is complete
      setQuizMode('results');
      
      // If this is a vocabulary quiz, complete the session and update progress
      if (vocabularySubcategoryId && autoSave) {
        autoSave.completeSession();
      }
    }
  };

  // Toggle explanation visibility
  const toggleExplanation = () => {
    setExplanationVisible(!explanationVisible);
  };

  // Save and exit the quiz (for mid-session exit)
  const saveAndExit = () => {
    // Ensure the current state is saved
    if (vocabularySubcategoryId && autoSave) {
      autoSave.saveSession();
    }
    
    // Return to category/subcategory selection
    if (quizMode === 'quiz' && vocabularySubcategoryId) {
      setQuizMode('vocab');
    } else {
      setQuizMode('category');
    }
    
    setVocabularySubcategoryId(null);
    setSelectedCategory(null);
    setCurrentQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedAnswer('');
    setIsAnswered(false);
    setExplanationVisible(false);
  };

  // Return to category selection
  const backToCategories = () => {
    // If this is a vocabulary quiz and in progress, offer to save
    if (quizMode === 'quiz' && vocabularySubcategoryId && currentQuestionIndex > 0 && 
        currentQuestionIndex < currentQuestions.length - 1) {
      if (window.confirm('Do you want to save your progress and exit? You can resume later.')) {
        saveAndExit();
      }
      return;
    }
    
    // Otherwise just go back
    if (quizMode === 'quiz' && vocabularySubcategoryId) {
      setQuizMode('vocab');
    } else {
      setQuizMode('category');
    }
    
    setVocabularySubcategoryId(null);
    setSelectedCategory(null);
    setCurrentQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedAnswer('');
    setIsAnswered(false);
    setExplanationVisible(false);
    
    // Clean up autosave
    if (autoSave) {
      autoSave.stopAutoSave();
      setAutoSave(null);
    }
  };

  // Loading state for quiz data
  if (loading && quizMode === 'category') {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div style={{ 
          width: '50px', 
          height: '50px', 
          margin: '0 auto',
          borderWidth: '4px', 
          borderStyle: 'solid',
          borderColor: '#e5e7eb',
          borderTopColor: '#1a56db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '16px' }}>Loading quiz categories...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="card" style={{ backgroundColor: '#fee2e2', borderColor: '#ef4444' }}>
        <h1 className="card-title" style={{ color: '#b91c1c' }}>Error</h1>
        <p>{error}</p>
        <button 
          className="button"
          onClick={() => window.location.reload()}
          style={{ marginTop: '16px' }}
        >
          Try Again
        </button>
      </div>
    );
  }

  // Render the current view based on quiz mode
  const renderContent = () => {
    switch (quizMode) {
      case 'category':
        // Find vocabulary category and other categories
        const vocabularyCategory = categories.find(cat => 
          cat.name.toLowerCase().includes('vocabulary') && cat.course_code === selectedCourse
        );
        const otherCategories = categories.filter(cat => 
          !cat.name.toLowerCase().includes('vocabulary')
        );
        
        return (
          <div className="card">
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px' 
            }}>
              <h2 className="card-title">{selectedCourse} Quiz Categories</h2>
              <button
                onClick={onBackToCourses}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  textDecoration: 'underline',
                  padding: '5px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                ← Back to Courses
              </button>
            </div>
            
            {/* Vocabulary Category - Special Styling */}
            {vocabularyCategory && (
              <div>
                <h3 style={{ 
                  fontSize: '20px', 
                  fontWeight: 'bold', 
                  marginBottom: '16px',
                  color: '#1e429f',
                  borderBottom: '2px solid #1e429f',
                  paddingBottom: '8px'
                }}>
                  Vocabulary
                </h3>
                <div 
                  key={vocabularyCategory.id}
                  style={{
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: '#bfdbfe',
                    borderRadius: '8px',
                    padding: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#eff6ff',
                    marginBottom: '24px'
                  }}
                  onClick={() => startCategoryQuiz(vocabularyCategory.id)}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#dbeafe';
                    e.currentTarget.style.borderColor = '#1a56db';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#eff6ff';
                    e.currentTarget.style.borderColor = '#bfdbfe';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h3 style={{ color: '#1e40af', marginBottom: '10px', fontWeight: 'bold' }}>{vocabularyCategory.name}</h3>
                  <p style={{ color: '#3b82f6', fontSize: '14px' }}>{vocabularyCategory.description}</p>
                </div>
              </div>
            )}
            
            {/* Review Questions Section */}
            {otherCategories.length > 0 && (
              <div>
                <h3 style={{ 
                  fontSize: '20px', 
                  fontWeight: 'bold', 
                  marginBottom: '16px',
                  color: '#1f2937',
                  borderBottom: '2px solid #1f2937',
                  paddingBottom: '8px'
                }}>
                  Review Questions
                </h3>
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '20px'
                }}>
                  {otherCategories.map(category => (
                    <div 
                      key={category.id}
                      style={{
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        borderColor: '#e5e7eb',
                        borderRadius: '8px',
                        padding: '20px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        backgroundColor: '#f9fafb'
                      }}
                      onClick={() => startCategoryQuiz(category.id)}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#f0f9ff';
                        e.currentTarget.style.borderColor = '#1a56db';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <h3 style={{ color: '#1a56db', marginBottom: '10px', fontWeight: 'bold' }}>{category.name}</h3>
                      <p style={{ color: '#6b7280', fontSize: '14px' }}>{category.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
        
      case 'vocab':
        return (
          <div className="card">
            <VocabularySubcategories onSelectSubcategory={handleVocabularySubcategorySelect}
            selectedCourse={selectedCourse} />
          </div>
        );
        
// Hardcoded mapping for vocab subcategory labels

case 'quiz':
  const currentQuestion = currentQuestions[currentQuestionIndex];
  return (
    <div className="card">
      {/* Top navigation bar with exit button */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '10px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <h2 className="card-title" style={{ margin: 0 }}>
          {selectedCategory?.name} 
          {vocabularySubcategoryId ? (() => {
            // Map subcategory ID to the correct label based on course
            let subcategoryLabels = {};
            
            if (selectedCourse === 'ASA101') {
              subcategoryLabels = {
                1: "A-C",
                2: "D-G", 
                3: "H-K",
                4: "L-N",
                5: "O-S",
                6: "T-Z"
              };
            } else if (selectedCourse === 'ASA103') {
              subcategoryLabels = {
                1: "Part 1",
                2: "Part 2",
                3: "Part 3"
              };
            } else {
              // Default fallback for other courses
              subcategoryLabels = {
                1: "Part 1",
                2: "Part 2",
                3: "Part 3"
              };
            }
            
            return ` (${subcategoryLabels[vocabularySubcategoryId] || `Part ${vocabularySubcategoryId}`})`;
          })() : ''}
        </h2>
        
        {/* Exit button clearly separated from other UI elements */}
        <button
          onClick={saveAndExit}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 12px',
            borderRadius: '4px',
            border: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            color: '#6b7280',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
            e.currentTarget.style.borderColor = '#d1d5db';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
            e.currentTarget.style.borderColor = '#e5e7eb';
          }}
        >
          {vocabularySubcategoryId ? 'Save & Exit' : 'Exit Quiz'}
        </button>
      </div>
      
      {/* Rest of the quiz content remains the same... */}
            
            <div style={{ 
              color: '#6b7280', 
              fontSize: '14px',
              marginBottom: '20px'
            }}>
              Question {currentQuestionIndex + 1} of {currentQuestions.length}
            </div>
            
            <div style={{ 
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: '#e5e7eb'
            }}>
              <p style={{ fontSize: '18px', marginBottom: '20px', fontWeight: '500' }}>
                {currentQuestion.question_text}
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                {currentQuestion.options.map((option, index) => {
                  // Define the base style with individual border properties 
                  // instead of the shorthand 'border' property
                  let style = {
                    padding: '15px',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: '#e5e7eb',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  };
                  
                  // Apply conditional styles based on selection state
                  if (selectedAnswer === option) {
                    if (isAnswered) {
                      if (selectedAnswer === currentQuestion.correct_answer) {
                        style.backgroundColor = '#d1fae5';
                        style.borderColor = '#10b981';
                      } else {
                        style.backgroundColor = '#fee2e2';
                        style.borderColor = '#ef4444';
                      }
                    } else {
                      style.backgroundColor = '#dbeafe';
                      style.borderColor = '#1a56db';
                    }
                  } else if (isAnswered && option === currentQuestion.correct_answer) {
                    style.backgroundColor = '#d1fae5';
                    style.borderColor = '#10b981';
                  }
                  
                  return (
                    <div 
                      key={index}
                      style={style}
                      onClick={() => !isAnswered && handleAnswerSelect(option)}
                    >
                      {option}
                    </div>
                  );
                })}
              </div>
              
              {!isAnswered ? (
                <button
                  className="button"
                  disabled={!selectedAnswer}
                  onClick={submitAnswer}
                  style={{ opacity: !selectedAnswer ? 0.5 : 1, cursor: !selectedAnswer ? 'not-allowed' : 'pointer' }}
                >
                  Submit Answer
                </button>
              ) : (
                <div style={{ marginTop: '20px' }}>
                  <button
                    onClick={toggleExplanation}
                    style={{ 
                      background: 'none',
                      border: 'none',
                      color: '#1a56db',
                      textDecoration: 'underline',
                      marginBottom: '15px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    {explanationVisible ? 'Hide Explanation' : 'Show Explanation'}
                  </button>
                  
                  {explanationVisible && (
                    <div style={{
                      backgroundColor: '#f9fafb',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: '#e5e7eb',
                      borderRadius: '6px',
                      padding: '15px',
                      margin: '15px 0',
                      fontSize: '15px'
                    }}>
                      <p style={{ color: '#4b5563' }}><strong>Explanation:</strong> {currentQuestion.explanation}</p>
                    </div>
                  )}
                  
                  <button
                    className="button"
                    onClick={nextQuestion}
                  >
                    {currentQuestionIndex < currentQuestions.length - 1 ? 'Next Question' : 'See Results'}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
        
      case 'results':
        const totalQuestions = currentQuestions.length;
        const percentCorrect = Math.round((results.correct / totalQuestions) * 100);
        const isPerfect = percentCorrect === 100;
        
        return (
          <div className="card">
            <h2 className="card-title">Quiz Results</h2>
            
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '20px',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: '#e5e7eb'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <div style={{ 
                  fontSize: '48px', 
                  fontWeight: 'bold', 
                  color: '#1a56db', 
                  marginBottom: '10px' 
                }}>
                  {percentCorrect}%
                </div>
                <p>{results.correct} correct out of {totalQuestions} questions</p>
              </div>
              
              {isPerfect ? (
                <div style={{
                  backgroundColor: '#d1fae5',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: '#10b981',
                  color: '#065f46',
                  padding: '20px',
                  textAlign: 'center',
                  borderRadius: '6px',
                  marginBottom: '30px',
                  fontWeight: 'bold',
                  fontSize: '18px'
                }}>
                  <p>Perfect Score! You're ready to sail!</p>
                </div>
              ) : (
                <div>
                  <h3 style={{ marginBottom: '15px', fontSize: '18px', fontWeight: 'bold' }}>Questions to Review:</h3>
                  <ul style={{ listStyleType: 'none', marginBottom: '30px' }}>
                    {results.incorrectQuestions.map((question, index) => (
                      <li key={index} style={{
                        padding: '15px',
                        borderLeftWidth: '4px',
                        borderLeftStyle: 'solid',
                        borderLeftColor: '#ef4444',
                        backgroundColor: '#fee2e2',
                        marginBottom: '10px',
                        borderRadius: '0 6px 6px 0'
                      }}>
                        {question.question_text}
                        <div style={{ fontSize: '14px', marginTop: '5px', color: '#b91c1c' }}>
                          Correct answer: {question.correct_answer}
                        </div>
                      </li>
                    ))}
                  </ul>
                  
                  {results.incorrectQuestions.length > 0 && (
                    <button
                      className="button"
                      onClick={startReviewQuiz}
                      style={{ marginBottom: '15px' }}
                    >
                      Practice Missed Questions
                    </button>
                  )}
                </div>
              )}
              
              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                marginTop: '20px',
                flexDirection: window.innerWidth < 768 ? 'column' : 'row' 
              }}>
                <button
                  className="button"
                  onClick={() => vocabularySubcategoryId ? 
                    startVocabularyQuiz(vocabularySubcategoryId) : 
                    startCategoryQuiz(selectedCategory.id)}
                  style={{ backgroundColor: '#1f2937' }}
                >
                  Retake Quiz
                </button>
                
                <button
                  className="button button-secondary"
                  onClick={backToCategories}
                >
                  Return to Categories
                </button>
              </div>
            </div>
          </div>
        );
        
      default:
        return <div>Something went wrong.</div>;
    }
  };

  return renderContent();
}

export default App;